import {
  ActionIdeaForm,
  CreateIdeaFormFieldTypes,
  CreateIdeaFormRef,
} from './action-idea-form.tsx'
import { UserInfo } from '../../../../../apis/auth/auth.def.ts'
import { useAppSelector } from '../../../../../redux/hooks.ts'
import { selectUser } from '../../../../../features/user/user.slice.ts'
import { FormInstance } from 'antd/lib/index'
import IdeyaModal from '../../../../../common/antd/ideya-modal.tsx'
import { IdeyaSpinner } from '../../../../../common/components/ideya-spinner.tsx'
import { useGetBookDetailsQuery } from '../../../../../apis/book/book-api.api.ts'
import { useGetIdeaDetailQuery } from '../../../../../apis/idea/idea-api.api.ts'
import { useEffect, useRef } from 'react'
import { LiquidateIdeaForm } from './liquidate-idea-form.tsx'
import { useIsMobile } from '../../../../../common/hooks/use-is-mobile.ts'
import './action-idea-modal.scss'
import { useTranslation } from 'react-i18next'

interface ActionIdeaModalProps {
  showActionModal: boolean
  showLiquidateModal: boolean
  setShowLiquidatedModal: (v: boolean) => void
  form: FormInstance<CreateIdeaFormFieldTypes>
  handleCloseModal: () => void
  onIdeaActioned: () => void
  editIdeaId?: string | null
  setEditIdeaId: (id: string | null) => void // <-- optional prop to set editIdeaId
}

export const ActionIdeaModal: React.FC<ActionIdeaModalProps> = ({
  showActionModal,
  showLiquidateModal,
  setShowLiquidatedModal,
  form,
  handleCloseModal,
  onIdeaActioned,
  editIdeaId, // <-- now this comes from parent and updates when setActionIdeaId is called
  setEditIdeaId,
}) => {
  const { t } = useTranslation()
  const userInfo: UserInfo = useAppSelector(selectUser)
  const {
    data,
    isFetching,
    isLoading,
    refetch: refetchGetBookDetailsQuery,
  } = useGetBookDetailsQuery(
    { id: userInfo.bookId },
    { skip: !userInfo.bookId }
  )

  const {
    currentData: editData,
    isLoading: isLoadingEdit,
    isFetching: isFetchingEdit,
    refetch: refetchGetIdeaDetailQuery,
  } = useGetIdeaDetailQuery(editIdeaId ?? '', {
    skip: !editIdeaId,
  })

  useEffect(() => {
    if (editData?.data) {
      form.setFieldsValue({
        figi: editData.data.ticker?.figi,
        direction: editData.data.direction,
        targetMarketValue: editData.data.targetMarketValue,
        target: editData.data.target,
        stop: editData.data.stop,
        fillStrategy: editData.data.fillStrategy,
        tradeReason: editData.data.tradeReason,
        horizon: editData.data.horizon,
        conviction: editData.data.conviction,
        comment: editData.data.description,
      })
    }
  }, [editIdeaId, editData, form])

  const createIdeaFormRef = useRef<CreateIdeaFormRef>(null)
  const isMobile = useIsMobile(768)

  const handleCloseAndClear = () => {
    setEditIdeaId(null)
    handleCloseModal()
    form.resetFields()
    createIdeaFormRef.current?.resetFormState()
  }

  return (
    <>
      <IdeyaModal
        open={showActionModal}
        title={
          editIdeaId
            ? t('idea_list.idea_modal.action_idea_modal.edit_idea')
            : t('idea_list.idea_modal.action_idea_modal.create_idea')
        }
        destroyOnClose
        isForm={true}
        width={isMobile ? '98vw' : 1300}
        onCancel={handleCloseAndClear}
        style={isMobile ? { top: 4, padding: 4 } : {}}
        className={'action-idea-modal'}
      >
        {isFetching || isLoading || isFetchingEdit || isLoadingEdit ? (
          <div
            style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
          >
            <IdeyaSpinner />
          </div>
        ) : (
          <ActionIdeaForm
            ref={createIdeaFormRef}
            form={form}
            onCancel={handleCloseAndClear}
            buyingPower={data?.data?.buyingPower}
            usedBuyingPower={data?.data?.usedBuyingPower}
            userInfo={userInfo}
            onIdeaActioned={() => {
              onIdeaActioned()
              refetchGetBookDetailsQuery()
              if (editIdeaId) {
                refetchGetIdeaDetailQuery()
              }
            }}
            onEditIdea={(ideaId: string) => setEditIdeaId(ideaId)}
            mode={editIdeaId ? 'edit' : 'create'}
            ideaDetail={editData?.data}
            onIdeaLiquidated={() => setShowLiquidatedModal(true)}
          />
        )}
      </IdeyaModal>
      <IdeyaModal
        open={showLiquidateModal}
        onCancel={() => setShowLiquidatedModal(false)}
        title={t('idea_list.idea_modal.action_idea_modal.liquidate_idea')}
        isForm={true}
        width={isMobile ? '95vw' : 500}
        zIndex={1500}
        style={isMobile ? { top: 8, padding: 8 } : {}}
      >
        <LiquidateIdeaForm
          ideaId={editIdeaId ?? ''}
          fillStrategy={editData?.data?.fillStrategy ?? ''}
          onIdeaActioned={onIdeaActioned}
          onCancel={() => setShowLiquidatedModal(false)}
          onSuccess={() => {
            setShowLiquidatedModal(false)
            handleCloseAndClear()
          }}
        />
      </IdeyaModal>
    </>
  )
}
